import { useCallback, useEffect, useState } from "react";

import { LimitedPropertyWithAgent } from "@/components/property/LimitedPropertiesSection";
import { PropallyApiFactory } from "@/module/CustomApiFactory";

type UseGetLimitedPropertiesOptions = {
  shuffle?: boolean;
};

interface UseGetLimitedPropertiesReturn {
  limitedProperties: LimitedPropertyWithAgent[];
  loading: boolean;
  error: boolean;
  refresh: () => Promise<void>;
}

const shuffleProperties = (properties: LimitedPropertyWithAgent[]): LimitedPropertyWithAgent[] => {
  const shuffled = [...properties];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const useGetLimitedProperties = ({
  shuffle = true,
}: UseGetLimitedPropertiesOptions = {}): UseGetLimitedPropertiesReturn => {
  const [limitedProperties, setLimitedProperties] = useState<LimitedPropertyWithAgent[]>([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchLimitedProperties = useCallback(async () => {
    const fp = PropallyApiFactory();
    setLoading(true);
    setError(false);
    try {
      const res = await fp.v1CompanyLimitedPropertiesGet({ withCredentials: true });
      if (res.status === 200) {
        const properties = (res.data as LimitedPropertyWithAgent[]) ?? [];
        setLimitedProperties(shuffle ? shuffleProperties(properties) : properties);
      } else {
        setError(true);
      }
    } catch (error) {
      console.error(error);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [shuffle]);

  useEffect(() => {
    fetchLimitedProperties();
  }, [fetchLimitedProperties]);

  return {
    limitedProperties,
    loading,
    error,
    refresh: fetchLimitedProperties,
  };
};
